import { Vector3 } from 'three'
import { formatPointLoadForceKn } from 'src/components/scene/LoadMesh/utils'
import { scaleVector } from 'src/components/scene/utils'

export interface HorizontalWindLoadArrow {
  id: string
  origin: Vector3
  direction: Vector3
  length: number
  force: Vector
}

const MIN_ARROW_LENGTH = 1.0e-3

export const HORIZONTAL_WIND_LOAD_COLOR = '#105ba4'

function buildHorizontalWindLoadArrow(
  id: string,
  force: Vector,
  pointOfAttack: Vector,
  z: number,
  loadDisplayScale: number,
): HorizontalWindLoadArrow | null {
  const loadPoint = new Vector3(pointOfAttack.x, pointOfAttack.y, z)
  const globalForce = new Vector3(force.x, force.y, 0)
  const scaledGlobalForce = scaleVector(globalForce, loadDisplayScale)

  if (scaledGlobalForce.length() < MIN_ARROW_LENGTH) {
    return null
  }

  const loadOrigin = loadPoint.clone().sub(scaledGlobalForce)
  const direction = scaledGlobalForce
  const length = direction.length()

  if (length < MIN_ARROW_LENGTH) {
    return null
  }

  return {
    id,
    origin: loadOrigin,
    direction: direction.normalize(),
    length,
    force,
  }
}

export function formatHorizontalWindLoadForceLabel(force: Vector): string[] {
  const xFormatted = formatPointLoadForceKn(force.x)
  const yFormatted = formatPointLoadForceKn(force.y)

  return [`X: ${xFormatted}`, `Y: ${yFormatted}`]
}

function getTopStoreyUpperZ(storeyBoundaries: StoreyBoundaries): number {
  const topStorey = Math.max(...Object.keys(storeyBoundaries).map(Number)).toString()
  return storeyBoundaries[topStorey][1]
}

export function buildHorizontalWindLoadArrows({
  ceilingLoads,
  roofLoads,
  slabToStorey,
  storeyBoundaries,
  loadDisplayScale,
}: {
  ceilingLoads: WindloadCeilingResult[]
  roofLoads: WindloadRoofResult[]
  slabToStorey: Record<string, string>
  storeyBoundaries: StoreyBoundaries
  loadDisplayScale: number
}): HorizontalWindLoadArrow[] {
  const roofZ = getTopStoreyUpperZ(storeyBoundaries)

  const ceilingArrows = ceilingLoads.flatMap((load, index) => {
    const storey = slabToStorey[load.slab_guid]
    const z = storey ? storeyBoundaries[storey]?.[1] : undefined

    if (z === undefined) {
      return []
    }

    const arrow = buildHorizontalWindLoadArrow(
      `ceiling-${load.slab_guid}-${index}`,
      load.force,
      load.point_of_attack,
      z,
      loadDisplayScale,
    )

    return arrow ? [arrow] : []
  })

  const roofArrows = roofLoads.flatMap((load, index) => {
    const arrow = buildHorizontalWindLoadArrow(
      `roof-${index}`,
      load.force,
      load.point_of_attack,
      roofZ,
      loadDisplayScale,
    )

    return arrow ? [arrow] : []
  })

  return [...ceilingArrows, ...roofArrows]
}
